import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Plus, Search } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { inDemo } from "@/lib/api";
import { ImageField } from "@/components/ImageField";
import { PageHeader, EmptyState, PageSkeleton } from "@/components/shared";
import { toast } from "sonner";

function Field({ field, value, onChange }) {
  const common = { id: field.key, "data-testid": `field-${field.key}` };
  if (field.type === "image") {
    return <ImageField value={value} onChange={onChange} testId={`field-${field.key}`} />;
  }
  if (field.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger {...common} className="rounded-xl"><SelectValue placeholder={`Select ${field.label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "checkbox") {
    return <div className="flex items-center gap-2 pt-1"><Checkbox {...common} checked={!!value} onCheckedChange={onChange} /><span className="text-sm text-muted-foreground">Yes</span></div>;
  }
  if (field.type === "textarea") {
    return <Textarea {...common} value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-xl" rows={3} />;
  }
  return <Input {...common} type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"} value={value ?? ""} onChange={(e) => onChange(field.type === "number" ? e.target.value : e.target.value)} className="rounded-xl" />;
}

export default function RecordManager({ entity, title, subtitle, fields, columns, icon, emptyLabel = "Add item", renderCell, defaults = {} }) {
  const { items, loading, create, update, remove } = useCollection(entity);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [query, setQuery] = useState("");

  const cols = columns || fields.slice(0, 4);

  const openNew = () => { setEditing(null); setForm({ ...defaults }); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setOpen(true); };

  const submit = async () => {
    if (inDemo()) { window.dispatchEvent(new CustomEvent("demo-locked")); setOpen(false); return; }
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.key];
      if (f.type === "number") v = v === "" || v == null ? 0 : Number(v);
      if (f.type === "checkbox") v = !!v;
      payload[f.key] = v ?? (f.type === "checkbox" ? false : "");
    });
    if (editing) { await update(editing.id, payload); toast.success("Updated"); }
    else { await create(payload); toast.success(`${title.replace(/s$/, "")} added`); }
    setOpen(false);
  };

  const filtered = items.filter((it) => {
    if (!query.trim()) return true;
    return Object.values(it).some((v) => String(v).toLowerCase().includes(query.toLowerCase()));
  });

  if (loading) return <PageSkeleton />;

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} testId={`${entity}-header`}
        action={<Button onClick={openNew} className="rounded-full" data-testid={`add-${entity}`}><Plus className="h-4 w-4 mr-1.5" />{emptyLabel}</Button>} />

      {items.length > 0 && (
        <div className="relative max-w-xs mb-4">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 rounded-full" data-testid={`search-${entity}`} />
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={icon} title={`No ${title.toLowerCase()} yet`} description={subtitle} actionLabel={emptyLabel} onAction={openNew} testId={`${entity}-empty`} />
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-left">
                  {cols.map((c) => <th key={c.key} className="px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">{c.label}</th>)}
                  <th className="px-4 py-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors" data-testid={`row-${entity}`}>
                    {cols.map((c) => (
                      <td key={c.key} className="px-4 py-3 align-top">{renderCell ? renderCell(c, it) : String(it[c.key] ?? "—") || "—"}</td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`edit-${entity}`}><Pencil className="h-4 w-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-${entity}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle><AlertDialogDescription>This can't be undone.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => { if (inDemo()) { window.dispatchEvent(new CustomEvent("demo-locked")); return; } remove(it.id); toast.success("Deleted"); }} data-testid="confirm-delete">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl">{editing ? `Edit ${title.replace(/s$/, "")}` : `Add ${title.replace(/s$/, "")}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Label htmlFor={f.key} className="text-xs text-muted-foreground">{f.label}</Label>
                <div className="mt-1"><Field field={f} value={form[f.key]} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} /></div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submit} className="rounded-full" data-testid={`save-${entity}`}>{editing ? "Save changes" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
